import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { UiProgressSpinner } from '@shared/components/ui';
import {
  EscanerCodigoService,
  ResultadoEscaneo,
} from '@shared/services/escaner-codigo.service';
import { esLecturaNueva } from '@shared/utils/escaner-codigo.utils';

@Component({
  selector: 'app-escaner-codigo',
  imports: [UiProgressSpinner],
  templateUrl: './escaner-codigo.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './escaner-codigo.css',
})
export class EscanerCodigo implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly escanerService = inject(EscanerCodigoService);
  private readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('video');

  titulo = input('Escanear producto');
  subtitulo = input('Apunta la cámara al código de barras');
  /** Aviso persistente sobre el visor (por ejemplo, producto no encontrado). */
  aviso = input<string | null>(null);
  /** Mientras está pausado, la cámara sigue visible pero no emite lecturas. */
  pausado = input(false);

  codigoDetectado = output<ResultadoEscaneo>();
  cerrar = output<void>();

  cargando = signal(true);
  error = signal<string | null>(null);
  linterna = signal(false);
  linternaDisponible = signal(false);
  ultimoCodigo = signal<string | null>(null);

  private stream: MediaStream | null = null;
  private detenerDeteccion: (() => void) | null = null;
  private ultimoCodigoVisto = '';
  private ultimoVistoTs = 0;
  private destruido = false;

  constructor() {
    // Al pausar o reanudar, reiniciamos el contador de rearme para que el mismo
    // código que sigue en el encuadre no se procese de inmediato otra vez.
    effect(() => {
      this.pausado();
      this.ultimoVistoTs = Date.now();
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    void this.iniciar();
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.liberarRecursos();
  }

  async alternarLinterna(): Promise<void> {
    if (!this.stream) return;

    try {
      this.linterna.set(await this.escanerService.alternarLinterna(this.stream));
    } catch {
      this.linterna.set(false);
      this.linternaDisponible.set(false);
    }
  }

  cerrarEscaner(): void {
    this.liberarRecursos();
    this.cerrar.emit();
  }

  private async iniciar(): Promise<void> {
    if (!this.escanerService.soportado) {
      this.error.set('Este dispositivo o navegador no permite usar la cámara.');
      this.cargando.set(false);
      return;
    }

    try {
      const stream = await this.escanerService.obtenerStream();

      if (this.destruido) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.stream = stream;
      const video = this.videoRef()?.nativeElement;
      if (!video) {
        this.error.set('No se pudo preparar el visor de la cámara.');
        return;
      }

      video.srcObject = stream;
      await video.play();

      this.linternaDisponible.set(this.escanerService.tieneLinterna(stream));
      const detener = await this.escanerService.iniciarDeteccion(video, (resultado) =>
        this.procesarDeteccion(resultado),
      );

      // El componente pudo cerrarse mientras se cargaba ZXing.
      if (this.destruido) {
        detener();
        return;
      }

      this.detenerDeteccion = detener;
    } catch (error) {
      this.error.set(this.mensajeDeError(error));
    } finally {
      this.cargando.set(false);
    }
  }

  private procesarDeteccion(resultado: ResultadoEscaneo): void {
    const codigo = resultado.codigo.trim();
    if (!codigo) return;

    const ahora = Date.now();
    const nuevaLectura = esLecturaNueva(
      codigo,
      { ultimoCodigo: this.ultimoCodigoVisto, ultimoVistoTs: this.ultimoVistoTs },
      ahora,
    );

    // Mientras el código siga en el encuadre, actualizamos su última vista.
    if (codigo === this.ultimoCodigoVisto || !this.pausado()) {
      this.ultimoCodigoVisto = codigo;
      this.ultimoVistoTs = ahora;
    }

    if (this.pausado() || !nuevaLectura) return;

    this.ultimoCodigo.set(codigo);
    this.emitirFeedback();
    this.codigoDetectado.emit({ ...resultado, codigo });
  }

  private emitirFeedback(): void {
    try {
      const contextoGlobal = window as Window & {
        webkitAudioContext?: typeof AudioContext;
      };
      const ConstructorAudio = window.AudioContext ?? contextoGlobal.webkitAudioContext;

      if (ConstructorAudio) {
        const contexto = new ConstructorAudio();
        const oscilador = contexto.createOscillator();
        const ganancia = contexto.createGain();

        oscilador.type = 'sine';
        oscilador.frequency.value = 880;
        ganancia.gain.setValueAtTime(0.08, contexto.currentTime);
        ganancia.gain.exponentialRampToValueAtTime(0.0001, contexto.currentTime + 0.12);
        oscilador.connect(ganancia);
        ganancia.connect(contexto.destination);
        oscilador.addEventListener('ended', () => void contexto.close());
        oscilador.start();
        oscilador.stop(contexto.currentTime + 0.13);
      }
    } catch {
      // El sonido es un extra: si falla, el escaneo sigue funcionando.
    }

    navigator.vibrate?.(40);
  }

  private liberarRecursos(): void {
    this.detenerDeteccion?.();
    this.detenerDeteccion = null;

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    const video = this.videoRef()?.nativeElement;
    if (video) {
      video.srcObject = null;
    }
  }

  private mensajeDeError(error: unknown): string {
    const nombre = (error as { name?: string } | null)?.name;

    switch (nombre) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Permiso de cámara denegado. Habilítalo en los ajustes del navegador.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'No se encontró una cámara disponible en este dispositivo.';
      case 'NotReadableError':
        return 'La cámara está siendo usada por otra aplicación.';
      default:
        return 'No se pudo iniciar la cámara. Intenta nuevamente.';
    }
  }
}
